<template>
  <div class="permit-type-summary">
    <!-- 화기작업 -->
    <template v-if="permKey === 'fireWorkPermit'">
      <table class="tbl v-tbl">
        <colgroup><col width="180px"><col></colgroup>
        <tbody>
          <tr><th>작업기간</th><td>{{ formatRange(data.workPeriod) }}</td></tr>
          <tr><th>화기작업 종류</th><td>{{ workTypesText(data) }}</td></tr>
          <tr><th>화재감시자</th><td>{{ data.fireWatcher || dash }}</td></tr>
          <tr><th>화재감시자 연락처</th><td>{{ data.fireWatcherPhone || dash }}</td></tr>
        </tbody>
      </table>
    </template>

    <!-- 고소작업 -->
    <template v-else-if="permKey === 'highAltitudePermit'">
      <table class="tbl v-tbl">
        <colgroup><col width="180px"><col></colgroup>
        <tbody>
          <tr><th>작업기간</th><td>{{ formatRange(data.workPeriod) }}</td></tr>
          <tr><th>고소작업 종류</th><td>{{ workTypesText(data) }}</td></tr>
        </tbody>
      </table>
    </template>

    <!-- 밀폐공간 -->
    <template v-else-if="permKey === 'confinedSpacePermit'">
      <table class="tbl v-tbl">
        <colgroup><col width="180px"><col></colgroup>
        <tbody>
          <tr><th>작업기간</th><td>{{ formatRange(data.workPeriod) }}</td></tr>
          <tr><th>밀폐공간 형태</th><td>{{ spaceTypesText(data) }}</td></tr>
        </tbody>
      </table>
    </template>

    <!-- 화학물질 -->
    <template v-else-if="permKey === 'chemicalPermit'">
      <table class="tbl v-tbl">
        <colgroup><col width="180px"><col></colgroup>
        <tbody>
          <tr><th>작업기간</th><td>{{ formatRange(data.workPeriod) }}</td></tr>
        </tbody>
      </table>
      <div class="sub-title mt-15">취급화학물질</div>
      <table class="tbl">
        <thead><tr><th>번호</th><th>화학물질</th><th>취급방법</th><th>위험성</th><th>MSDS</th></tr></thead>
        <tbody>
          <tr v-for="(c, ci) in (data.chemicals || [])" :key="'chem-'+ci">
            <td>{{ ci + 1 }}</td>
            <td>{{ c.name || dash }}</td>
            <td>{{ c.handlingMethods || dash }}</td>
            <td>{{ c.hazard || dash }}</td>
            <td>{{ fileList(c.msdsFiles) }}</td>
          </tr>
          <tr v-if="!(data.chemicals || []).length"><td colspan="5" class="text-888">데이터 없음</td></tr>
        </tbody>
      </table>
    </template>

    <!-- 전기작업 -->
    <template v-else-if="permKey === 'electricWorkPermit'">
      <table class="tbl v-tbl">
        <colgroup><col width="180px"><col></colgroup>
        <tbody>
          <tr><th>작업기간</th><td>{{ formatRange(data.workPeriod) }}</td></tr>
          <tr><th>작업범위</th><td>{{ data.workScope || dash }}</td></tr>
        </tbody>
      </table>
      <div class="sub-title mt-15">작업자 자격확인</div>
      <table class="tbl">
        <thead><tr><th>성명</th><th>자격</th></tr></thead>
        <tbody>
          <tr v-for="(w, wi) in (data.workers || [])" :key="'w-'+wi">
            <td>{{ w.name || dash }}</td><td>{{ w.qualification || dash }}</td>
          </tr>
          <tr v-if="!(data.workers || []).length"><td colspan="2" class="text-888">데이터 없음</td></tr>
        </tbody>
      </table>
      <div class="sub-title mt-15">보호구 및 사용장비</div>
      <table class="tbl v-tbl">
        <colgroup><col width="180px"><col></colgroup>
        <tbody>
          <tr><th>접근 한계거리</th><td>{{ data.approachDistance || dash }}</td></tr>
          <tr><th>지급 보호구</th><td>{{ protectionsText(data) }}</td></tr>
          <tr><th>측정장비</th><td>{{ data.measurementEquip || dash }}</td></tr>
          <tr><th>활선기구/장비</th><td>{{ data.liveLineEquip || dash }}</td></tr>
          <tr><th>방호구</th><td>{{ data.defenseEquip || dash }}</td></tr>
          <tr><th>기타</th><td>{{ data.otherEquip || dash }}</td></tr>
        </tbody>
      </table>
      <div class="sub-title mt-15">작업순서</div>
      <permit-procedure-table :procedures="data.procedures"></permit-procedure-table>
      <div class="sub-title mt-15">첨부파일</div>
      <table class="tbl v-tbl">
        <colgroup><col width="180px"><col></colgroup>
        <tbody>
          <tr><th>설비 관련</th><td>{{ joinWithOther(data.attachments && data.attachments.facility, data.attachments && data.attachments.facilityOther, data.attachments && data.attachments.facilityOtherText) }}</td></tr>
          <tr><th>안전 관련</th><td>{{ joinWithOther(data.attachments && data.attachments.safety, data.attachments && data.attachments.safetyOther, data.attachments && data.attachments.safetyOtherText) }}</td></tr>
        </tbody>
      </table>
    </template>

    <!-- 운전중작업 -->
    <template v-else-if="permKey === 'operatingWorkPermit'">
      <table class="tbl v-tbl">
        <colgroup><col width="180px"><col></colgroup>
        <tbody>
          <tr><th>작업기간</th><td>{{ formatRange(data.workPeriod) }}</td></tr>
        </tbody>
      </table>
      <div class="sub-title mt-15">운전중 작업 설비</div>
      <table class="tbl">
        <thead><tr><th>번호</th><th>설비명</th><th>작업범위</th><th>작업내용</th></tr></thead>
        <tbody>
          <tr v-for="(f, fi) in (data.facilities || [])" :key="'f-'+fi">
            <td>{{ fi + 1 }}</td>
            <td>{{ f.facilityName || dash }}</td>
            <td>{{ f.workScope || dash }}</td>
            <td>{{ f.workContent || dash }}</td>
          </tr>
          <tr v-if="!(data.facilities || []).length"><td colspan="4" class="text-888">데이터 없음</td></tr>
        </tbody>
      </table>
    </template>

    <!-- 중량물취급 / 굴착 / 차량계하역 / 이동식크레인 / 차량계건설 / 지게차 / 차량형 / 시저형 -->
    <template v-else>
      <heavy-permit-summary :data="data" :perm-key="permKey"></heavy-permit-summary>
    </template>
  </div>
</template>

<script>
import moment from 'moment';
import HeavyPermitSummary from './HeavyPermitSummary.vue';
import PermitProcedureTable from './PermitProcedureTable.vue';

export default {
  name: 'PermitTypeSummary',
  components: { HeavyPermitSummary, PermitProcedureTable },
  props: {
    permKey: { type: String, required: true },
    data: { type: Object, default: () => ({}) }
  },
  data() {
    return { dash: '-' };
  },
  methods: {
    chipList(arr) {
      if (!arr || !arr.length) return this.dash;
      return arr.join(', ');
    },
    workTypesText(d) {
      const arr = (d && d.workTypes) || [];
      const out = [...arr];
      if (d && d.isOther && d.otherText) out.push('기타: ' + d.otherText);
      return out.length ? out.join(', ') : this.dash;
    },
    spaceTypesText(d) {
      const arr = (d && d.spaceTypes) || [];
      const out = [...arr];
      if (d && d.isOther && d.otherText) out.push('기타: ' + d.otherText);
      return out.length ? out.join(', ') : this.dash;
    },
    protectionsText(d) {
      const arr = (d && d.protections) || [];
      const out = [...arr];
      if (d && d.isProtectionOther && d.protectionOtherText) out.push('기타: ' + d.protectionOtherText);
      return out.length ? out.join(', ') : this.dash;
    },
    joinWithOther(arr, otherFlag, otherText) {
      const out = [...(arr || [])];
      if (otherFlag && otherText) out.push('기타: ' + otherText);
      return out.length ? out.join(', ') : this.dash;
    },
    fileList(arr) {
      if (!arr || !arr.length) return this.dash;
      return arr.map((f) => f.name || '').filter(Boolean).join(', ');
    },
    formatRange(range) {
      if (!range || !range.length) return this.dash;
      const f = range[0] ? moment(range[0]).format('YYYY-MM-DD') : '';
      const t = range[1] ? moment(range[1]).format('YYYY-MM-DD') : '';
      return f && t ? `${f} ~ ${t}` : (f || t || this.dash);
    }
  }
};
</script>

<style scoped>
.sub-title {
  font-weight: 600;
  font-size: 13px;
  color: #333;
  border-bottom: 1px solid #e2e2e2;
  padding-bottom: 4px;
  margin-bottom: 8px;
}
.permit-type-summary > .sub-title:first-child {
  margin-top: 0;
}
</style>